import { api } from "api";
import { delay } from "@vyne-shared/utils/delay";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

/**
 * Client hook to query for existing Insurance Carriers when entering a new payment request.
 * The query is debounced, by waiting the prescribed amount of time before making the request,
 * and canceling any previous requests as applicable.
 * Analogous to usePatientQuery.
 * @param searchFilter Required filter string to perform the query.
 * @param excludeUnlinked If set, P/RPs not linked to PMS patients are not included.
 * @param debounceDelay Number of milliseconds to wait before actually querying. 0 to disable debounce.
 * @returns
 *  - React Query properties
 *  - isFilterValid: boolean: conveys whether or not the supplied search filter was valid enough to
 *    actually perform the search.
 */
export function useInsuranceQuery(insuranceCarrier: string, debounceDelay: number) {
    const { isFetching, data } = useQuery({
        queryKey: ["searchPatientField", insuranceCarrier, debounceDelay],
        queryFn: async (context) => {
            // bail if filter invalid or debounce delay interrupted due to cancellation
            if (
                insuranceCarrier.length < 3 ||
                (insuranceCarrier.length > 0 && (await delay(debounceDelay, context.signal)))
            ) {
                return null;
            }

            return await api.provider.queryInsurance(insuranceCarrier, context.signal);
        },
        placeholderData: keepPreviousData,
    });

    return { isFetching, data } as const;
}
